// Bounded deterministic locomotion measurement trace (Gate 0 foundation).
//
// Records per-fixed-step samples for certification. Fields that are not yet
// observable are explicit nulls plus a `missingMeasurements` list — never
// fabricated. Samples are quantized before hashing for stable determinism.

export const LOCOMOTION_TRACE_MAX_SAMPLES = 720; // 12 s @ 60 Hz
export const LOCOMOTION_TRACE_REPORT_VERSION = 1 as const;

/** Quantization scales (units → fixed-point integers for hashing). */
export const LOCOMOTION_TRACE_QUANT = {
  positionM: 1e4, // 0.1 mm
  velocityMps: 1e4,
  angleDeg: 1e3,
  timeS: 1e6,
  unitless: 1e6,
  impulseNs: 1e5,
} as const;

export type LocomotionVec3 = readonly [number, number, number];

export interface LocomotionCommandSample {
  /** Desired forward speed (m/s). null when no locomotion command loop exists. */
  forwardMps: number | null;
  lateralMps: number | null;
  yawRateRadS: number | null;
  stop: boolean | null;
  /**
   * Explicit one-shot swing-foot forward displacement command (m).
   * Gate A records 0.22 here so the trace is not only zero-velocity + stop.
   * null when no one-shot displacement command is active.
   */
  oneShotForwardDisplacementM: number | null;
  /** Explicit one-shot swing-foot lateral displacement command (m). */
  oneShotLateralDisplacementM: number | null;
}

export interface LocomotionFootSample {
  position: LocomotionVec3 | null;
  velocity: LocomotionVec3 | null;
  contact: boolean | null;
  consecutiveAirborneSteps: number | null;
  consecutivePlantSteps: number | null;
  /** Impulse-bearing Box3D manifold points on this foot after the step. */
  contactPointCount: number | null;
  /** Sum of post-solver normal impulse over those points (N·s). */
  totalNormalImpulse: number | null;
  slipM: number | null;
  lastHitPoint: LocomotionVec3 | null;
  lastHitNormal: LocomotionVec3 | null;
  lastHitApproachSpeed: number | null;
  /**
   * Foot yaw about world +Y (rad), diagnostic only.
   * Extracted from bone pose; excluded from FOOT_KEYS / quantized payload so
   * instrumentation cannot change physics-identity hashes.
   */
  yawRad?: number | null;
  /**
   * Sole pitch about world +X (rad), diagnostic only (Gate A push 19).
   * 0 = flat sole; + = toe up; − = toe down. Not hashed.
   */
  pitchRad?: number | null;
}

export interface LocomotionJointMotorSample {
  /** Joint actual/target error (rad or m). null when not instrumented. */
  maxTrackingError: number | null;
  meanTrackingError: number | null;
  /** Motor speed command / torque cap / saturation fraction when available. */
  motorSpeed: number | null;
  torqueCap: number | null;
  /**
   * Actual solver motor torque (N·m): max |revolute| / spherical magnitude
   * among active joints this step. Never a commanded substitute.
   */
  actualTorque: number | null;
  saturationFraction: number | null;
  /** Active motors this step (torqueCap > 0). */
  activeMotorCount: number | null;
  /** Motors at ≥98% of commanded maxTorque this step. */
  saturatedMotorCount: number | null;
}

/**
 * Per-DOF lateral command as issued by the muscle controller this fixed step.
 * Diagnostic only — never hashed into sample stateHash / physics identity.
 */
export interface LocomotionLateralDofCommand {
  /** Rotation-vector Z target (rad). null when joint command absent. */
  targetLatRad: number | null;
  /**
   * Optional per-command motor speed cap (rad/s) when the controller set
   * `maxSpeedRadS`. null when uncapped / not set (solver PD speed is computed
   * downstream in the motor bridge and is not re-derived here).
   */
  commandedTargetSpeedRadS: number | null;
  /** Commanded maxTorqueNm for this joint this frame. */
  maxTorqueNm: number | null;
  /**
   * Post-solver saturation flag for this joint this fixed step (diagnostic).
   * null when motor telemetry is unavailable or the joint was not active.
   * Never hashed into sample stateHash.
   */
  saturated?: boolean | null;
}

/** P1A in-place load-policy lateral overlays (rad) when that path ran. */
export interface LocomotionP1ALoadLateralPolicyOverlay {
  hipLatOverlayLeft: number;
  hipLatOverlayRight: number;
  ankleLatLeft: number;
  ankleLatRight: number;
  spineLat: number;
  /**
   * Plant-IK hip lateral baseline (rad) before COM overlay — diagnostic only.
   * null when the load-acceptance path did not run this frame.
   */
  plantHipLatLeft?: number | null;
  plantHipLatRight?: number | null;
  /**
   * Plant-IK hip sagittal baselines + issued sagittal targets (rad) — diagnostic
   * only (Gate A push-5 forward-load attribution). Never used by control.
   */
  plantHipSagLeft?: number | null;
  plantHipSagRight?: number | null;
  issuedHipSagLeft?: number | null;
  issuedHipSagRight?: number | null;
  issuedKneeLeft?: number | null;
  issuedKneeRight?: number | null;
  issuedAnkleSagLeft?: number | null;
  issuedAnkleSagRight?: number | null;
  issuedSpineSag?: number | null;
  /** com.z − midPlant.z (m); positive ⇒ COM forward of plant mid. */
  comMinusMidPlantZM?: number | null;
  /** true when forward residual sagittal path ran (commandedForward > 0). */
  forwardPlantSagittal?: boolean | null;
}

/**
 * Sign convention for `opposesComError` (load-phase lateral attribution):
 *
 * - World +X = right. `comMinusSupportLateralXM = com.x − support.x`
 *   (positive ⇒ COM right of support center). Derived from dual-support /
 *   load mid-plant usage in `p1aInPlaceLoadAcceptancePolicy`
 *   (`humanoidMuscleController.ts`, errX = comX − midPlantX).
 * - Lateral joint commands are parent-local rotation-vector Z (rad).
 *   FK note in `prepareLateralTargets`: lat+ couples thigh/pelvis toward +X.
 * - `commandCorrection` for each DOF is the signed issued lateral target Z
 *   (same units as the motor command). No jointSign remapping: the raw
 *   issued target is the attribution channel.
 * - `opposesComError = sign(commandCorrection) !== sign(comMinusSupportX)`
 *   when both are nonzero; false if either is ~0 (no directional claim).
 * - "Reinforces" = same nonzero sign as comMinusSupportX (actively drives
 *   further in the error direction under this convention).
 */
export function lateralCommandOpposesComError(
  commandCorrectionRad: number | null | undefined,
  comMinusSupportLateralXM: number | null | undefined,
  eps = 1e-12,
): boolean | null {
  if (
    commandCorrectionRad == null ||
    comMinusSupportLateralXM == null ||
    !Number.isFinite(commandCorrectionRad) ||
    !Number.isFinite(comMinusSupportLateralXM)
  ) {
    return null;
  }
  if (Math.abs(commandCorrectionRad) <= eps || Math.abs(comMinusSupportLateralXM) <= eps) {
    return false;
  }
  const sCmd = commandCorrectionRad > 0 ? 1 : -1;
  const sErr = comMinusSupportLateralXM > 0 ? 1 : -1;
  return sCmd !== sErr;
}

/**
 * Additive load-phase lateral drive attribution sample (diagnostic only).
 * Excluded from quantized sample payload / stateHash so instrumentation
 * cannot change physics-identity hashes.
 */
export interface LocomotionLateralAttributionSample {
  comLateralXM: number | null;
  comLateralVelocityMps: number | null;
  comMinusSupportLateralXM: number | null;
  supportMarginM: number | null;
  leftLoadFraction: number | null;
  rightLoadFraction: number | null;
  leftContact: boolean | null;
  rightContact: boolean | null;
  leftHip: LocomotionLateralDofCommand;
  rightHip: LocomotionLateralDofCommand;
  leftAnkleRoll: LocomotionLateralDofCommand;
  rightAnkleRoll: LocomotionLateralDofCommand;
  spine: LocomotionLateralDofCommand;
  /** Non-null only when P1A in-place load acceptance path ran this frame. */
  p1aLoadPolicyLateral: LocomotionP1ALoadLateralPolicyOverlay | null;
  opposesComError: {
    leftHip: boolean | null;
    rightHip: boolean | null;
    leftAnkleRoll: boolean | null;
    rightAnkleRoll: boolean | null;
    spine: boolean | null;
  };
}

export interface LocomotionTraceSample {
  step: number;
  timeS: number;
  command: LocomotionCommandSample;
  /** High-level muscle/locomotion phase string when available. */
  phase: string | null;
  stepPhase: string | null;
  swingSide: string | null;
  transitionReason: string | null;
  pelvisPosition: LocomotionVec3 | null;
  pelvisVelocity: LocomotionVec3 | null;
  pelvisTiltDeg: number | null;
  comPosition: LocomotionVec3 | null;
  comVelocity: LocomotionVec3 | null;
  capturePoint: LocomotionVec3 | null;
  supportCenter: LocomotionVec3 | null;
  supportMarginM: number | null;
  desiredVelocityMps: LocomotionVec3 | null;
  leftFoot: LocomotionFootSample;
  rightFoot: LocomotionFootSample;
  /** Planned swing-foot target (world). */
  targetFootPosition: LocomotionVec3 | null;
  targetFootClamped: boolean | null;
  actualFootPathErrorM: number | null;
  clearanceM: number | null;
  touchdownErrorM: number | null;
  jointMotor: LocomotionJointMotorSample;
  maxJointSeparationM: number | null;
  nonFootGroundContactCount: number | null;
  forbiddenAssistCount: number | null;
  /**
   * Optional load/lateral command attribution (diagnostic). null when not
   * captured. Never participates in sample stateHash.
   */
  lateralAttribution: LocomotionLateralAttributionSample | null;
  /** Explicit list of fields not yet measurable in this build. */
  missingMeasurements: readonly string[];
  /** Hash of the quantized sample payload (excludes this field). */
  stateHash: string;
}

export interface LocomotionTraceReport {
  reportVersion: typeof LOCOMOTION_TRACE_REPORT_VERSION;
  fixedDtSeconds: number;
  maxSamples: number;
  sampleCount: number;
  droppedSamples: number;
  firstFailureReason: string | null;
  pass: boolean;
  /** Ordered contact-trace hash when available. */
  contactTraceHash: string | null;
  /** Final sample hash chain. */
  traceHash: string | null;
  missingMeasurements: readonly string[];
  samples: readonly LocomotionTraceSample[];
  notes: readonly string[];
}

export interface LocomotionTraceBuffer {
  readonly maxSamples: number;
  readonly sampleCount: number;
  readonly droppedSamples: number;
  readonly fixedDtSeconds: number;
  reset(fixedDtSeconds?: number): void;
  push(partial: LocomotionTraceSampleInput): LocomotionTraceSample;
  samples(): readonly LocomotionTraceSample[];
  /** FNV-1a over all quantized sample hashes in order. */
  traceHash(): string;
  toReport(args?: {
    pass?: boolean;
    firstFailureReason?: string | null;
    contactTraceHash?: string | null;
    notes?: readonly string[];
  }): LocomotionTraceReport;
}

/** Input before quantization/hash; missing fields become null + missing list. */
export interface LocomotionTraceSampleInput {
  step: number;
  timeS: number;
  command?: Partial<LocomotionCommandSample> | null;
  phase?: string | null;
  stepPhase?: string | null;
  swingSide?: string | null;
  transitionReason?: string | null;
  pelvisPosition?: LocomotionVec3 | null;
  pelvisVelocity?: LocomotionVec3 | null;
  pelvisTiltDeg?: number | null;
  comPosition?: LocomotionVec3 | null;
  comVelocity?: LocomotionVec3 | null;
  capturePoint?: LocomotionVec3 | null;
  supportCenter?: LocomotionVec3 | null;
  supportMarginM?: number | null;
  desiredVelocityMps?: LocomotionVec3 | null;
  leftFoot?: Partial<LocomotionFootSample> | null;
  rightFoot?: Partial<LocomotionFootSample> | null;
  targetFootPosition?: LocomotionVec3 | null;
  targetFootClamped?: boolean | null;
  actualFootPathErrorM?: number | null;
  clearanceM?: number | null;
  touchdownErrorM?: number | null;
  jointMotor?: Partial<LocomotionJointMotorSample> | null;
  maxJointSeparationM?: number | null;
  nonFootGroundContactCount?: number | null;
  forbiddenAssistCount?: number | null;
  /** Optional diagnostic lateral attribution (not hashed). */
  lateralAttribution?: LocomotionLateralAttributionSample | null;
  /** Additional caller-declared missing fields (merged with auto-detected). */
  extraMissingMeasurements?: readonly string[];
}

// ---------------------------------------------------------------------------
// Diagnostic-only lateral command side-channel (does not affect physics).
// Muscle controller publishes issued laterals each control step; locomotion
// trace push auto-attaches when present so evaluation need not change.
// ---------------------------------------------------------------------------

/** Controller-published lateral targets for one fixed step (pre-attribution). */
export interface LocomotionLateralCommandPublish {
  leftHip: LocomotionLateralDofCommand;
  rightHip: LocomotionLateralDofCommand;
  leftAnkleRoll: LocomotionLateralDofCommand;
  rightAnkleRoll: LocomotionLateralDofCommand;
  spine: LocomotionLateralDofCommand;
  p1aLoadPolicyLateral: LocomotionP1ALoadLateralPolicyOverlay | null;
}

let pendingLateralPublish: LocomotionLateralCommandPublish | null = null;

/** Publish issued lateral joint commands for the current control step. */
export function publishLateralCommandSnapshot(
  snap: LocomotionLateralCommandPublish | null,
): void {
  pendingLateralPublish = snap;
}

/** Read (without clearing) the last published lateral command snapshot. */
export function peekLateralCommandSnapshot(): LocomotionLateralCommandPublish | null {
  return pendingLateralPublish;
}

/** Clear the pending lateral publish (e.g. between trials). */
export function clearLateralCommandSnapshot(): void {
  pendingLateralPublish = null;
}

function emptyDofCommand(): LocomotionLateralDofCommand {
  return {
    targetLatRad: null,
    commandedTargetSpeedRadS: null,
    maxTorqueNm: null,
    saturated: null,
  };
}

/**
 * Build a full attribution sample from published joint commands + live kinematics.
 * Pure; does not touch physics.
 */
export function buildLateralAttributionSample(args: {
  publish: LocomotionLateralCommandPublish | null;
  comPosition: LocomotionVec3 | null | undefined;
  comVelocity: LocomotionVec3 | null | undefined;
  supportCenter: LocomotionVec3 | null | undefined;
  supportMarginM: number | null | undefined;
  leftFoot: Pick<LocomotionFootSample, 'contact' | 'totalNormalImpulse'> | null | undefined;
  rightFoot: Pick<LocomotionFootSample, 'contact' | 'totalNormalImpulse'> | null | undefined;
}): LocomotionLateralAttributionSample | null {
  if (!args.publish) return null;
  const comX = args.comPosition?.[0] ?? null;
  const comVx = args.comVelocity?.[0] ?? null;
  const supportX = args.supportCenter?.[0] ?? null;
  const comMinusSupportX =
    comX != null && supportX != null && Number.isFinite(comX) && Number.isFinite(supportX)
      ? comX - supportX
      : null;
  const leftImp = args.leftFoot?.totalNormalImpulse;
  const rightImp = args.rightFoot?.totalNormalImpulse;
  let leftLoad: number | null = null;
  let rightLoad: number | null = null;
  if (
    leftImp != null &&
    rightImp != null &&
    Number.isFinite(leftImp) &&
    Number.isFinite(rightImp) &&
    leftImp + rightImp > 0
  ) {
    const sum = leftImp + rightImp;
    leftLoad = leftImp / sum;
    rightLoad = rightImp / sum;
  }
  const p = args.publish;
  return {
    comLateralXM: comX,
    comLateralVelocityMps: comVx,
    comMinusSupportLateralXM: comMinusSupportX,
    supportMarginM: args.supportMarginM ?? null,
    leftLoadFraction: leftLoad,
    rightLoadFraction: rightLoad,
    leftContact: args.leftFoot?.contact ?? null,
    rightContact: args.rightFoot?.contact ?? null,
    leftHip: p.leftHip,
    rightHip: p.rightHip,
    leftAnkleRoll: p.leftAnkleRoll,
    rightAnkleRoll: p.rightAnkleRoll,
    spine: p.spine,
    p1aLoadPolicyLateral: p.p1aLoadPolicyLateral,
    opposesComError: {
      leftHip: lateralCommandOpposesComError(p.leftHip.targetLatRad, comMinusSupportX),
      rightHip: lateralCommandOpposesComError(p.rightHip.targetLatRad, comMinusSupportX),
      leftAnkleRoll: lateralCommandOpposesComError(
        p.leftAnkleRoll.targetLatRad,
        comMinusSupportX,
      ),
      rightAnkleRoll: lateralCommandOpposesComError(
        p.rightAnkleRoll.targetLatRad,
        comMinusSupportX,
      ),
      spine: lateralCommandOpposesComError(p.spine.targetLatRad, comMinusSupportX),
    },
  };
}

export function emptyLateralDofCommand(): LocomotionLateralDofCommand {
  return emptyDofCommand();
}

const FOOT_KEYS: (keyof LocomotionFootSample)[] = [
  'position',
  'velocity',
  'contact',
  'consecutiveAirborneSteps',
  'consecutivePlantSteps',
  'contactPointCount',
  'totalNormalImpulse',
  'slipM',
  'lastHitPoint',
  'lastHitNormal',
  'lastHitApproachSpeed',
];

const JOINT_KEYS: (keyof LocomotionJointMotorSample)[] = [
  'maxTrackingError',
  'meanTrackingError',
  'motorSpeed',
  'torqueCap',
  'actualTorque',
  'saturationFraction',
  'activeMotorCount',
  'saturatedMotorCount',
];

function emptyFoot(): LocomotionFootSample {
  return {
    position: null,
    velocity: null,
    contact: null,
    consecutiveAirborneSteps: null,
    consecutivePlantSteps: null,
    contactPointCount: null,
    totalNormalImpulse: null,
    slipM: null,
    lastHitPoint: null,
    lastHitNormal: null,
    lastHitApproachSpeed: null,
  };
}

function emptyJointMotor(): LocomotionJointMotorSample {
  return {
    maxTrackingError: null,
    meanTrackingError: null,
    motorSpeed: null,
    torqueCap: null,
    actualTorque: null,
    saturationFraction: null,
    activeMotorCount: null,
    saturatedMotorCount: null,
  };
}

function mergeFoot(partial?: Partial<LocomotionFootSample> | null): LocomotionFootSample {
  const base = emptyFoot();
  if (!partial) return base;
  return { ...base, ...partial };
}

function mergeJoint(
  partial?: Partial<LocomotionJointMotorSample> | null,
): LocomotionJointMotorSample {
  const base = emptyJointMotor();
  if (!partial) return base;
  return { ...base, ...partial };
}

function isMissing(value: unknown): boolean {
  return value === null || value === undefined;
}

function collectMissing(sample: Omit<LocomotionTraceSample, 'stateHash' | 'missingMeasurements'>): string[] {
  const missing: string[] = [];
  const cmd = sample.command;
  if (isMissing(cmd.forwardMps)) missing.push('command.forwardMps');
  if (isMissing(cmd.lateralMps)) missing.push('command.lateralMps');
  if (isMissing(cmd.yawRateRadS)) missing.push('command.yawRateRadS');
  if (isMissing(cmd.stop)) missing.push('command.stop');
  if (isMissing(cmd.oneShotForwardDisplacementM)) missing.push('command.oneShotForwardDisplacementM');
  if (isMissing(cmd.oneShotLateralDisplacementM)) missing.push('command.oneShotLateralDisplacementM');
  if (isMissing(sample.phase)) missing.push('phase');
  if (isMissing(sample.stepPhase)) missing.push('stepPhase');
  if (isMissing(sample.swingSide)) missing.push('swingSide');
  if (isMissing(sample.transitionReason)) missing.push('transitionReason');
  if (isMissing(sample.pelvisPosition)) missing.push('pelvisPosition');
  if (isMissing(sample.pelvisVelocity)) missing.push('pelvisVelocity');
  if (isMissing(sample.pelvisTiltDeg)) missing.push('pelvisTiltDeg');
  if (isMissing(sample.comPosition)) missing.push('comPosition');
  if (isMissing(sample.comVelocity)) missing.push('comVelocity');
  if (isMissing(sample.capturePoint)) missing.push('capturePoint');
  if (isMissing(sample.supportCenter)) missing.push('supportCenter');
  if (isMissing(sample.supportMarginM)) missing.push('supportMarginM');
  if (isMissing(sample.desiredVelocityMps)) missing.push('desiredVelocityMps');
  for (const key of FOOT_KEYS) {
    if (isMissing(sample.leftFoot[key])) missing.push(`leftFoot.${key}`);
    if (isMissing(sample.rightFoot[key])) missing.push(`rightFoot.${key}`);
  }
  if (isMissing(sample.targetFootPosition)) missing.push('targetFootPosition');
  if (isMissing(sample.targetFootClamped)) missing.push('targetFootClamped');
  if (isMissing(sample.actualFootPathErrorM)) missing.push('actualFootPathErrorM');
  if (isMissing(sample.clearanceM)) missing.push('clearanceM');
  if (isMissing(sample.touchdownErrorM)) missing.push('touchdownErrorM');
  for (const key of JOINT_KEYS) {
    if (isMissing(sample.jointMotor[key])) missing.push(`jointMotor.${key}`);
  }
  if (isMissing(sample.maxJointSeparationM)) missing.push('maxJointSeparationM');
  if (isMissing(sample.nonFootGroundContactCount)) missing.push('nonFootGroundContactCount');
  if (isMissing(sample.forbiddenAssistCount)) missing.push('forbiddenAssistCount');
  return missing;
}

export function quantizeNumber(value: number, scale: number): number {
  if (!Number.isFinite(value)) return 0;
  // Normalize -0 → 0 before quantizing.
  const v = Object.is(value, -0) ? 0 : value;
  return Math.round(v * scale);
}

export function quantizeVec3(
  value: LocomotionVec3 | null | undefined,
  scale: number,
): readonly [number, number, number] | null {
  if (!value) return null;
  return [
    quantizeNumber(value[0], scale),
    quantizeNumber(value[1], scale),
    quantizeNumber(value[2], scale),
  ];
}

/** Stable FNV-1a 32-bit of a JSON-canonical quantized payload. */
export function hashQuantizedPayload(payload: unknown): string {
  const text = JSON.stringify(payload);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function quantizedFoot(foot: LocomotionFootSample): unknown {
  return {
    position: quantizeVec3(foot.position, LOCOMOTION_TRACE_QUANT.positionM),
    velocity: quantizeVec3(foot.velocity, LOCOMOTION_TRACE_QUANT.velocityMps),
    contact: foot.contact,
    consecutiveAirborneSteps: foot.consecutiveAirborneSteps,
    consecutivePlantSteps: foot.consecutivePlantSteps,
    contactPointCount: foot.contactPointCount,
    totalNormalImpulse:
      foot.totalNormalImpulse === null || foot.totalNormalImpulse === undefined
        ? null
        : quantizeNumber(foot.totalNormalImpulse, LOCOMOTION_TRACE_QUANT.impulseNs),
    slipM:
      foot.slipM === null || foot.slipM === undefined
        ? null
        : quantizeNumber(foot.slipM, LOCOMOTION_TRACE_QUANT.positionM),
    lastHitPoint: quantizeVec3(foot.lastHitPoint, LOCOMOTION_TRACE_QUANT.positionM),
    lastHitNormal: quantizeVec3(foot.lastHitNormal, LOCOMOTION_TRACE_QUANT.unitless),
    lastHitApproachSpeed:
      foot.lastHitApproachSpeed === null || foot.lastHitApproachSpeed === undefined
        ? null
        : quantizeNumber(foot.lastHitApproachSpeed, LOCOMOTION_TRACE_QUANT.velocityMps),
  };
}

export function buildQuantizedSamplePayload(
  sample: Omit<LocomotionTraceSample, 'stateHash'>,
): unknown {
  return {
    step: sample.step,
    timeS: quantizeNumber(sample.timeS, LOCOMOTION_TRACE_QUANT.timeS),
    command: {
      forwardMps:
        sample.command.forwardMps === null
          ? null
          : quantizeNumber(sample.command.forwardMps, LOCOMOTION_TRACE_QUANT.velocityMps),
      lateralMps:
        sample.command.lateralMps === null
          ? null
          : quantizeNumber(sample.command.lateralMps, LOCOMOTION_TRACE_QUANT.velocityMps),
      yawRateRadS:
        sample.command.yawRateRadS === null
          ? null
          : quantizeNumber(sample.command.yawRateRadS, LOCOMOTION_TRACE_QUANT.unitless),
      stop: sample.command.stop,
      oneShotForwardDisplacementM:
        sample.command.oneShotForwardDisplacementM === null
          ? null
          : quantizeNumber(
              sample.command.oneShotForwardDisplacementM,
              LOCOMOTION_TRACE_QUANT.positionM,
            ),
      oneShotLateralDisplacementM:
        sample.command.oneShotLateralDisplacementM === null
          ? null
          : quantizeNumber(
              sample.command.oneShotLateralDisplacementM,
              LOCOMOTION_TRACE_QUANT.positionM,
            ),
    },
    phase: sample.phase,
    stepPhase: sample.stepPhase,
    swingSide: sample.swingSide,
    transitionReason: sample.transitionReason,
    pelvisPosition: quantizeVec3(sample.pelvisPosition, LOCOMOTION_TRACE_QUANT.positionM),
    pelvisVelocity: quantizeVec3(sample.pelvisVelocity, LOCOMOTION_TRACE_QUANT.velocityMps),
    pelvisTiltDeg:
      sample.pelvisTiltDeg === null
        ? null
        : quantizeNumber(sample.pelvisTiltDeg, LOCOMOTION_TRACE_QUANT.angleDeg),
    comPosition: quantizeVec3(sample.comPosition, LOCOMOTION_TRACE_QUANT.positionM),
    comVelocity: quantizeVec3(sample.comVelocity, LOCOMOTION_TRACE_QUANT.velocityMps),
    capturePoint: quantizeVec3(sample.capturePoint, LOCOMOTION_TRACE_QUANT.positionM),
    supportCenter: quantizeVec3(sample.supportCenter, LOCOMOTION_TRACE_QUANT.positionM),
    supportMarginM:
      sample.supportMarginM === null
        ? null
        : quantizeNumber(sample.supportMarginM, LOCOMOTION_TRACE_QUANT.positionM),
    desiredVelocityMps: quantizeVec3(
      sample.desiredVelocityMps,
      LOCOMOTION_TRACE_QUANT.velocityMps,
    ),
    leftFoot: quantizedFoot(sample.leftFoot),
    rightFoot: quantizedFoot(sample.rightFoot),
    targetFootPosition: quantizeVec3(
      sample.targetFootPosition,
      LOCOMOTION_TRACE_QUANT.positionM,
    ),
    targetFootClamped: sample.targetFootClamped,
    actualFootPathErrorM:
      sample.actualFootPathErrorM === null
        ? null
        : quantizeNumber(sample.actualFootPathErrorM, LOCOMOTION_TRACE_QUANT.positionM),
    clearanceM:
      sample.clearanceM === null
        ? null
        : quantizeNumber(sample.clearanceM, LOCOMOTION_TRACE_QUANT.positionM),
    touchdownErrorM:
      sample.touchdownErrorM === null
        ? null
        : quantizeNumber(sample.touchdownErrorM, LOCOMOTION_TRACE_QUANT.positionM),
    jointMotor: {
      maxTrackingError:
        sample.jointMotor.maxTrackingError === null
          ? null
          : quantizeNumber(sample.jointMotor.maxTrackingError, LOCOMOTION_TRACE_QUANT.unitless),
      meanTrackingError:
        sample.jointMotor.meanTrackingError === null
          ? null
          : quantizeNumber(sample.jointMotor.meanTrackingError, LOCOMOTION_TRACE_QUANT.unitless),
      motorSpeed:
        sample.jointMotor.motorSpeed === null
          ? null
          : quantizeNumber(sample.jointMotor.motorSpeed, LOCOMOTION_TRACE_QUANT.unitless),
      torqueCap:
        sample.jointMotor.torqueCap === null
          ? null
          : quantizeNumber(sample.jointMotor.torqueCap, LOCOMOTION_TRACE_QUANT.unitless),
      actualTorque:
        sample.jointMotor.actualTorque === null
          ? null
          : quantizeNumber(sample.jointMotor.actualTorque, LOCOMOTION_TRACE_QUANT.unitless),
      saturationFraction:
        sample.jointMotor.saturationFraction === null
          ? null
          : quantizeNumber(sample.jointMotor.saturationFraction, LOCOMOTION_TRACE_QUANT.unitless),
      activeMotorCount: sample.jointMotor.activeMotorCount,
      saturatedMotorCount: sample.jointMotor.saturatedMotorCount,
    },
    maxJointSeparationM:
      sample.maxJointSeparationM === null
        ? null
        : quantizeNumber(sample.maxJointSeparationM, LOCOMOTION_TRACE_QUANT.positionM),
    nonFootGroundContactCount: sample.nonFootGroundContactCount,
    forbiddenAssistCount: sample.forbiddenAssistCount,
    missingMeasurements: sample.missingMeasurements,
  };
}

export function finalizeLocomotionTraceSample(
  input: LocomotionTraceSampleInput,
): LocomotionTraceSample {
  const command: LocomotionCommandSample = {
    forwardMps: input.command?.forwardMps ?? null,
    lateralMps: input.command?.lateralMps ?? null,
    yawRateRadS: input.command?.yawRateRadS ?? null,
    stop: input.command?.stop ?? null,
    oneShotForwardDisplacementM: input.command?.oneShotForwardDisplacementM ?? null,
    oneShotLateralDisplacementM: input.command?.oneShotLateralDisplacementM ?? null,
  };
  const leftFoot = mergeFoot(input.leftFoot);
  const rightFoot = mergeFoot(input.rightFoot);
  // Explicit input wins; else auto-attach controller-published laterals (if any).
  // Consume the side-channel on attach so stale publishes cannot leak across
  // samples or unit tests. Explicit null/object skips the channel entirely.
  let lateralAttribution: LocomotionLateralAttributionSample | null;
  if (input.lateralAttribution !== undefined) {
    lateralAttribution = input.lateralAttribution;
  } else {
    lateralAttribution = buildLateralAttributionSample({
      publish: pendingLateralPublish,
      comPosition: input.comPosition,
      comVelocity: input.comVelocity,
      supportCenter: input.supportCenter,
      supportMarginM: input.supportMarginM,
      leftFoot,
      rightFoot,
    });
    pendingLateralPublish = null;
  }
  const draft: Omit<LocomotionTraceSample, 'stateHash' | 'missingMeasurements'> = {
    step: input.step | 0,
    timeS: input.timeS,
    command,
    phase: input.phase ?? null,
    stepPhase: input.stepPhase ?? null,
    swingSide: input.swingSide ?? null,
    transitionReason: input.transitionReason ?? null,
    pelvisPosition: input.pelvisPosition ?? null,
    pelvisVelocity: input.pelvisVelocity ?? null,
    pelvisTiltDeg: input.pelvisTiltDeg ?? null,
    comPosition: input.comPosition ?? null,
    comVelocity: input.comVelocity ?? null,
    capturePoint: input.capturePoint ?? null,
    supportCenter: input.supportCenter ?? null,
    supportMarginM: input.supportMarginM ?? null,
    desiredVelocityMps: input.desiredVelocityMps ?? null,
    leftFoot,
    rightFoot,
    targetFootPosition: input.targetFootPosition ?? null,
    targetFootClamped: input.targetFootClamped ?? null,
    actualFootPathErrorM: input.actualFootPathErrorM ?? null,
    clearanceM: input.clearanceM ?? null,
    touchdownErrorM: input.touchdownErrorM ?? null,
    jointMotor: mergeJoint(input.jointMotor),
    maxJointSeparationM: input.maxJointSeparationM ?? null,
    nonFootGroundContactCount: input.nonFootGroundContactCount ?? null,
    forbiddenAssistCount: input.forbiddenAssistCount ?? null,
    lateralAttribution,
  };

  const autoMissing = collectMissing(draft);
  const extra = input.extraMissingMeasurements ?? [];
  const missingSet = new Set<string>([...autoMissing, ...extra]);
  const missingMeasurements = [...missingSet].sort();
  const withMissing = { ...draft, missingMeasurements };
  // lateralAttribution is intentionally excluded from the quantized payload.
  const stateHash = hashQuantizedPayload(buildQuantizedSamplePayload(withMissing));
  return { ...withMissing, stateHash };
}

export function createLocomotionTraceBuffer(
  maxSamples = LOCOMOTION_TRACE_MAX_SAMPLES,
  fixedDtSeconds = 1 / 60,
): LocomotionTraceBuffer {
  let samples: LocomotionTraceSample[] = [];
  let droppedSamples = 0;
  let dt = fixedDtSeconds;
  const cap = Math.max(1, maxSamples | 0);

  return {
    get maxSamples() {
      return cap;
    },
    get sampleCount() {
      return samples.length;
    },
    get droppedSamples() {
      return droppedSamples;
    },
    get fixedDtSeconds() {
      return dt;
    },

    reset(nextDt?: number): void {
      samples = [];
      droppedSamples = 0;
      if (nextDt !== undefined) dt = nextDt;
    },

    push(partial: LocomotionTraceSampleInput): LocomotionTraceSample {
      const sample = finalizeLocomotionTraceSample(partial);
      if (samples.length >= cap) {
        samples.shift();
        droppedSamples += 1;
      }
      samples.push(sample);
      return sample;
    },

    samples(): readonly LocomotionTraceSample[] {
      return samples;
    },

    traceHash(): string {
      let hash = 0x811c9dc5;
      for (const sample of samples) {
        for (let i = 0; i < sample.stateHash.length; i += 1) {
          hash ^= sample.stateHash.charCodeAt(i);
          hash = Math.imul(hash, 0x01000193) >>> 0;
        }
      }
      return hash.toString(16).padStart(8, '0');
    },

    toReport(args = {}): LocomotionTraceReport {
      const missingUnion = new Set<string>();
      for (const s of samples) {
        for (const m of s.missingMeasurements) missingUnion.add(m);
      }
      const pass = args.pass ?? false;
      const firstFailureReason =
        args.firstFailureReason !== undefined
          ? args.firstFailureReason
          : pass
            ? null
            : 'locomotion gate not evaluated or not passed';
      return {
        reportVersion: LOCOMOTION_TRACE_REPORT_VERSION,
        fixedDtSeconds: dt,
        maxSamples: cap,
        sampleCount: samples.length,
        droppedSamples,
        firstFailureReason,
        pass,
        contactTraceHash: args.contactTraceHash ?? null,
        traceHash: samples.length > 0 ? this.traceHash() : null,
        missingMeasurements: [...missingUnion].sort(),
        samples: samples.slice(),
        notes: args.notes ?? [
          'Gate 0 measurement foundation — samples are quantized before hashing.',
          'Null fields and missingMeasurements list mean unavailable, not zero.',
          'A non-passing baseline must never be presented as locomotion success.',
        ],
      };
    },
  };
}

export function serializeLocomotionTraceReport(report: LocomotionTraceReport): string {
  return JSON.stringify(
    report,
    (_key, value) => {
      if (typeof value === 'number' && Object.is(value, -0)) return 0;
      return value;
    },
    2,
  );
}
